import React from "react"
import type {
  EstablishmentData, MunicipalityLookup, ResidueGroup, TechnicianData, WizardArchetype
} from "../../types"
import { GROUP_META } from "./constants"

interface Props {
  archetype: WizardArchetype | null
  establishment: EstablishmentData
  technician: TechnicianData
  groups: ResidueGroup[]
  answeredCount: number
  municipality: MunicipalityLookup | null
}

// O documento oficial nascendo: cada resposta carimba um grupo no plano.
// As seções pagas aparecem trancadas (linhas borradas — nunca o texto real).
export default function DocumentPreview({
  archetype, establishment, technician, groups, answeredCount, municipality
}: Props) {
  const placeholder = "text-gray-400"

  return (
    <div className="paper-preview rounded-lg px-6 py-7 sm:px-8">
      {/* Cabeçalho do documento */}
      <div className="border-b-2 border-ink/10 pb-4 text-center">
        <p className="text-[0.65rem] uppercase tracking-[0.2em] text-brand-700 font-semibold">
          Plano de Gerenciamento de Resíduos de Serviços de Saúde
        </p>
        <h2 className="mt-2 text-xl font-semibold text-ink leading-tight tracking-tight">
          {establishment.fantasy_name || <span className={placeholder}>Seu estabelecimento</span>}
        </h2>
        <p className="mt-1 text-xs text-gray-500">
          {archetype ? archetype.label : "—"}
          {establishment.municipio && <> · {establishment.municipio}/{establishment.uf}</>}
        </p>
      </div>

      {/* 1. Identificação */}
      <DocSection number="1" title="Identificação do Gerador">
        <p className="text-xs text-gray-600 leading-relaxed">
          {establishment.fantasy_name || <span className={placeholder}>nome fantasia…</span>}
          {establishment.cnpj && <> · CNPJ {establishment.cnpj}</>}
          {establishment.logradouro && <> · {establishment.logradouro}</>}
        </p>
      </DocSection>

      {/* 2. RT */}
      <DocSection number="2" title="Responsável Técnico">
        {technician.name ? (
          <p className="text-xs text-gray-600">
            {technician.name} — {technician.council}/{technician.uf} {technician.council_number}
          </p>
        ) : (
          <p className={`text-xs ${placeholder}`}>preenchido na etapa do RT…</p>
        )}
      </DocSection>

      {/* 3. Classificação — os carimbos */}
      <DocSection number="3" title="Classificação dos Resíduos (RDC 222)">
        <div className="flex flex-wrap gap-2">
          {groups.map(group => (
            <span
              key={group}
              className={`animate-stamp inline-flex items-center gap-1.5 rounded border-2 px-2.5 py-1 text-[0.7rem] font-bold uppercase tracking-wide ${GROUP_META[group].stamp}`}
              title={GROUP_META[group].description}
            >
              {GROUP_META[group].name}
            </span>
          ))}
        </div>
        {answeredCount === 0 && (
          <p className={`mt-2 text-xs ${placeholder}`}>
            responda o questionário e veja os grupos sendo identificados…
          </p>
        )}
        {groups.length > 1 && (
          <ul className="mt-3 space-y-1">
            {groups.map(group => (
              <li key={group} className="text-[0.7rem] text-gray-500">
                <span className="font-semibold text-gray-700">{GROUP_META[group].name}:</span>{" "}
                {GROUP_META[group].description}
              </li>
            ))}
          </ul>
        )}
      </DocSection>

      {/* 4. Manejo/POPs — trancado */}
      <DocSection number="4" title="Manejo e POPs" locked>
        <p className="text-[0.7rem] text-gray-500 mb-2">
          Segregação, acondicionamento, coleta, armazenamento e destinação — um POP por grupo identificado.
        </p>
        <LockedLines count={Math.max(groups.length * 2, 4)} />
      </DocSection>

      {/* 5. Município — trancado */}
      <DocSection number="5" title="Requisitos do Município" locked>
        {municipality?.requires_engineer_art && (
          <p className="mb-2 text-[0.7rem] font-semibold text-accent-600">
            ⚠ Seu município pode exigir ART de engenheiro — detalhado no plano.
          </p>
        )}
        <LockedLines count={3} />
      </DocSection>

      <div className="mt-5 border-t-2 border-ink/10 pt-4 flex items-center justify-between">
        <p className="text-[0.65rem] text-gray-400">Conforme RDC ANVISA 222/2018</p>
        <p className="text-[0.65rem] font-semibold text-brand-700">Preview parcial gratuito</p>
      </div>
    </div>
  )
}

function DocSection({ number, title, locked, children }: {
  number: string, title: string, locked?: boolean, children: React.ReactNode
}) {
  return (
    <section className="mt-5">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
        <span className="text-brand-600">{number}.</span> {title}
        {locked && (
          <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-ink/5 px-2 py-0.5 text-[0.6rem] font-sans font-semibold uppercase tracking-wide text-gray-500">
            🔒 no plano completo
          </span>
        )}
      </h3>
      <div className="mt-2">{children}</div>
    </section>
  )
}

function LockedLines({ count }: { count: number }) {
  return (
    <div className="space-y-2" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="locked-line" style={{ width: `${88 - (i % 3) * 14}%` }} />
      ))}
    </div>
  )
}
